import spartaGlobalFunctions from '@sparta.global.utilities/sparta-global-functions-utility/dist/es6.js';

/**
 * Helper to prepend CDN or webhost to asset URLs.
 * @param {String} path Unique id of the footnote
 * @returns {String} Absolute asset URL, prepended with CDN or webhost
 */
function assetUrl(path) {
  // Expecting a string - return the unmodified param if it's not a string.
  if (typeof path !== 'string') return path;
  const assetExtensions = [
  // images
  '.jpg', '.jpeg', '.webp', '.png', '.gif', '.png', '.svg',
  // videos
  '.webm', '.mp4',
  // documents
  '.csv', '.doc', '.docx', '.pdf', '.ppt', '.pptx', '.xls', '.xlsx'];

  // Expecting the string to contain an allowed asset extension.
  // Return the unmodified param if it does not contain an extension.
  if (!assetExtensions.some(extension => path.includes(extension))) return path;

  // Expecting the string to NOT start with http (absolute reference to externally hosted image)
  // Return the unmodified param if it begins with 'http'
  const protocol = 'http';
  if (path.slice(0, 4) === protocol) return path;

  // Expecting the string to begin with a '/' for consistency
  if (path.slice(0, 1) !== '/') {
    path = `/${path}`; // eslint-disable-line no-param-reassign
  }

  // Using the 'official' Sparta function to prepend the correct domain
  // Use the CDN if the environment CDN is enabled AND the CDN host is defined
  let domain = spartaGlobalFunctions.getSpaAssetsUrlPrefix();

  // If domain ends in '/' remove this character as the relative path will contain a leading '/'
  if (domain.slice(-1) === '/') {
    domain = domain.slice(0, -1);
  }
  let buildId;

  // Get the Sparta build ID and append as a query param to ensure cache freshness
  if (document.body.dataset.buildId) {
    buildId = parseInt(document.body.dataset.buildId, 0);
  } else {
    buildId = null;
  }
  const absolutePath = `${domain}${path}?${buildId}`;
  return absolutePath;
}
/**
 * Helper to get the file name from a path.
 * @param {String} path the asset path
 * @returns {String} the filename
 */
function fileName(path) {
  const str = path.substring(path.lastIndexOf('/')).replace('/assets-images-site-targeted-content-', '');
  const containerStartIdx = str.indexOf('bac');
  const containerEndIdx = str.indexOf('-', containerStartIdx);
  const site = str.substring(0, containerStartIdx - 1);
  const container = str.substring(containerStartIdx, containerEndIdx);
  const fileNameStr = str.substring(containerEndIdx + 1);
  return `
  <ul class='assets-filename-list'>
    <li>${site}</li>
    <li>${container}</li>
    <li>${fileNameStr}</li>
  </ul>`;
}
/**
 * Helper to construct pagination markup from given paginated results.
 * @param {Object} pages object returned from the paginate utility function.
 * @returns {String} markup for the page's pagination section.
 */
function paginate(pages) {
  const {
    currentPage,
    previousPage,
    nextPage,
    numPages
  } = pages;
  let paginatedMarkup = '';

  /**
  |--------------------------------------------------
  | Three types of pagination, based on current page.
  | If 4 pages or less, display all at once.
  |--------------------------------------------------
  */

  // First 3 pages
  if (numPages > 4) {
    if (currentPage < 4) {
      Array.from(Array(4).keys()).forEach(page => {
        paginatedMarkup += `
          <a id="page${page}" class="page-number nav-link${currentPage === page ? ' current' : ''}">${page}</a>
        `;
      });
      paginatedMarkup += `
        <span class="pagination-ellipsis">...</span>
        <a id="page${numPages}" class="page-number nav-link final">${numPages}</a>
      `;
    } else if (currentPage >= 4 && currentPage < numPages - 2) {
      // Pages from 4 to 2 below max
      paginatedMarkup += `
        <a id="page1" class="page-number nav-link first">1</a>
        <span class="pagination-ellipsis">...</span>
        <a id="page${previousPage}" class="page-number nav-link">${previousPage}</a>
        <a id="page${currentPage}" class="page-number nav-link current">${currentPage}</a>
        <a id="page${nextPage}" class="page-number nav-link">${nextPage}</a>
        <span class="pagination-ellipsis">...</span>
        <a id="page${numPages}" class="page-number nav-link final">${numPages}</a>
      `;
    } else if (currentPage >= numPages - 2) {
      // Last four pages
      paginatedMarkup += `
        <a id="page1" class="page-number nav-link first">1</a>
        <span class="pagination-ellipsis">...</span>
      `;
      [numPages - 3, numPages - 2, numPages - 1, numPages].forEach(page => {
        paginatedMarkup += `
          <a id="page${page}" class="page-number nav-link${currentPage === page ? ' current' : ''}">${page}</a>
        `;
      });
    }
  } else {
    for (let page = 1; page <= numPages; page++) {
      paginatedMarkup += `
        <a id="page${page}" class="page-number nav-link${currentPage === page ? ' current' : ''}">${page}</a>
      `;
    }
  }
  return paginatedMarkup;
}

export { assetUrl, fileName, paginate };
